import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import Handsontable from 'handsontable';
import 'handsontable/dist/handsontable.full.css';
import styled from 'styled-components';

const SpreadsheetContainer = styled.div`
  overflow: hidden;
  height: ${props => props.height}px;
`;

const Spreadsheet = ({
  columns,
  data,
  height,
  onChange,
  showColHeaders,
  showRowHeaders,
  startCols,
  startRows,
}) => {
  const containerElement = useRef();
  const spreadsheetRef = useRef(null);

  const onChangeRef = useRef(onChange);
  const columnsRef = useRef(columns);
  const optionsRef = useRef({
    showColHeaders,
    showRowHeaders,
    startCols,
    startRows,
  });
  const dataRef = useRef(data);

  useEffect(() => {
    onChangeRef.current = onChange;
    columnsRef.current = columns;
    optionsRef.current = {
      showColHeaders,
      showRowHeaders,
      startCols,
      startRows,
    };
    dataRef.current = data;
  });

  useEffect(() => {
    const { current: handleChange } = onChangeRef ?? {};
    const { current: providedColumns } = columnsRef ?? {};
    const { current: providedData } = dataRef ?? {};
    const { current: options = {} } = optionsRef ?? {};
    const instance = new Handsontable(containerElement.current, {
      colHeaders: providedColumns ?? options?.showColHeaders ?? true,
      rowHeaders: options?.showRowHeaders ?? true,
      startRows: options?.startRows ?? 15,
      startCols: providedColumns?.length ?? options?.startCols ?? 20,
      stretchH: 'all',
      minSpareRows: 1,
      data: providedData,
    });

    instance.addHook('afterChange', () => {
      const rows = instance.getData();
      const [firstRow] = rows ?? [];
      const validColumnKeys = (firstRow ?? []).reduce((acc, value, columnKey) => {
        if (instance.isEmptyCol(columnKey)) {
          return acc;
        }

        return [...acc, columnKey];
      }, []);
      const columnHeaders = (firstRow ?? []).reduce(
        (acc, value, columnKey) => ({ ...acc, [columnKey]: instance.getColHeader(columnKey) }),
        {},
      );
      const formattedData = rows.reduce((rowAcc, row, rowKey) => {
        if (instance.isEmptyRow(rowKey)) {
          return rowAcc;
        }

        const values = row.reduce((columnAcc, value, columnKey) => {
          if (!validColumnKeys.includes(columnKey)) {
            return columnAcc;
          }

          const columnName = columnHeaders[columnKey] ?? columnKey;
          return { ...columnAcc, [columnName]: value };
        }, {});

        return [...rowAcc, values];
      }, []);

      if (handleChange && typeof handleChange === 'function') {
        handleChange(formattedData);
      }
    });

    spreadsheetRef.current = instance;

    return () => {
      instance.destroy();
    };
  }, []);

  useEffect(() => {
    const { current: instance } = spreadsheetRef ?? {};
    const { current: providedData = null } = dataRef ?? {};

    if (instance && providedData?.length > 0) {
      instance.loadData(providedData);
    }
  }, []);

  return <SpreadsheetContainer ref={containerElement} height={height} />;
};

Spreadsheet.propTypes = {
  columns: PropTypes.array,
  data: PropTypes.array,
  height: PropTypes.number,
  onChange: PropTypes.func,
  showColHeaders: PropTypes.bool,
  showRowHeaders: PropTypes.bool,
  startCols: PropTypes.number,
  startRows: PropTypes.number,
};

Spreadsheet.defaultProps = {
  columns: undefined,
  data: null,
  height: 300,
  onChange: undefined,
  showColHeaders: true,
  showRowHeaders: true,
  startCols: 20,
  startRows: 15,
};

export default Spreadsheet;
